/**
 * Named-waypoint registry hook (plan §Phase 4D location autocomplete).
 *
 * Static reference data — no live overlay, unlike `useDoors`/`useLifts`.
 * Powers `TaskDispatchForm.tsx`'s location dropdown/autocomplete and
 * lets it validate a typed place name against the real building
 * layout before allowing submission, instead of accepting any string
 * and only failing once the worker tries (and fails) to dispatch it
 * against RMF.
 */
import { useQuery } from '@tanstack/react-query';
import { useSafeSiteId } from '../../state/useActiveSite';
import { fetchPlaces } from '../v1-client';
import type { PlaceRegistry, SiteId } from '../v1';

export function usePlaces() {
  const siteId = useSafeSiteId();
  return useQuery<PlaceRegistry[]>({
    queryKey: ['places', siteId],
    queryFn: () => fetchPlaces(siteId as SiteId),
    enabled: Boolean(siteId),
    // Building geometry changes only on a worker restart / map
    // republish, not per-request — avoid refetching on every focus.
    staleTime: 60_000,
  });
}
